import { DateTimeValueSchemas } from "@validereinc/utilities";
import { z } from "zod";
import {
  createCreateOneRequestSchema,
  createDeleteOneRequestSchema,
  createGetListRequestSchema,
  createGetOneRequestSchema,
  createUpdateOneRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
} from "../util/index.ts";
import { AssetTypeSchema } from "./AssetSchemas.ts";
import { ReportingGroupCreateOneRequestSchema } from "./ReportingGroupSchemas.ts";

export const EstimationMethodStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;
export type EstimationMethodStatusType =
  (typeof EstimationMethodStatus)[keyof typeof EstimationMethodStatus];

export const EstimationMethodInputSchema = z.object({
  value: z.union([z.number(), z.string()]),
  unit: z.string(),
  measurement_type: z.string().optional(),
  measurement_quantity: z.string().optional(),
});
export type EstimationMethodInputType = z.infer<
  typeof EstimationMethodInputSchema
>;

export const EstimationMethodSchema = z
  .object({
    company_id: z.string().uuid(),
    status: z.nativeEnum(EstimationMethodStatus),
    entity_type: AssetTypeSchema,
    entity_id: z.string().uuid(),
    analytics_library_id: z.string(),
    analytics_calculator_id: z.string(),
    name: z.string(),
    default_values: z.record(EstimationMethodInputSchema),
    default_asset_values: z.record(
      z.record(z.string().uuid(), EstimationMethodInputSchema)
    ),
    reporting_groups: z.array(ReportingGroupCreateOneRequestSchema.shape.data),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe(
    "Represents an estimation method in the system, which can be used for various calculations and analytics related to assets."
  );
export type EstimationMethodType = z.infer<typeof EstimationMethodSchema>;

export const EstimationMethodGetListRequestSchema = createGetListRequestSchema(
  EstimationMethodSchema.pick({
    entity_id: true,
    entity_type: true,
  }).partial(),
  z
    .object({
      period: DateTimeValueSchemas.dateYearMonth,
      isExcelFile: z.boolean().optional(),
    })
    .partial()
);
export type EstimationMethodGetListRequestType = z.infer<
  typeof EstimationMethodGetListRequestSchema
>;

export const EstimationMethodGetOneRequestSchema = createGetOneRequestSchema(
  z
    .object({
      entityType: AssetTypeSchema,
      period: DateTimeValueSchemas.dateYearMonth,
    })
    .partial()
);
export type EstimationMethodGetOneRequestType = z.infer<
  typeof EstimationMethodGetOneRequestSchema
>;

export const EstimationMethodCreateOneRequestSchema =
  createCreateOneRequestSchema(
    EstimationMethodSchema.pick({
      entity_id: true,
      analytics_library_id: true,
      analytics_calculator_id: true,
      name: true,
      default_values: true,
      default_asset_values: true,
    }),
    z.object({
      entityType: AssetTypeSchema,
    })
  );
export type EstimationMethodCreateOneRequestType = z.infer<
  typeof EstimationMethodCreateOneRequestSchema
>;

export const EstimationMethodUpdateOneRequestSchema =
  createUpdateOneRequestSchema(
    EstimationMethodSchema.pick({
      entity_id: true,
      name: true,
      default_values: true,
      default_asset_values: true,
      status: true,
    })
      .merge(
        z.object({
          start_date: DateTimeValueSchemas.dateTime,
          end_date: DateTimeValueSchemas.dateTime,
          delete_configurations_and_results: z.boolean(),
        })
      )
      .partial(),
    z.object({
      entityType: AssetTypeSchema,
    })
  );
export type EstimationMethodUpdateOneRequestType = z.infer<
  typeof EstimationMethodUpdateOneRequestSchema
>;

export const EstimationMethodDeleteOneRequestSchema =
  createDeleteOneRequestSchema(
    EstimationMethodSchema,
    z.object({
      entityType: AssetTypeSchema,
    })
  );
export type EstimationMethodDeleteOneRequestType = z.infer<
  typeof EstimationMethodDeleteOneRequestSchema
>;

export const NetworkMeasurementType = {
  volume: "volume",
  mass: "mass",
  energy: "energy",
} as const;

export type NetworkMeasurementTypeType =
  (typeof NetworkMeasurementType)[keyof typeof NetworkMeasurementType];

export const NetworkCalculatorId = {
  [NetworkMeasurementType.volume]: "FuelProrationEquationC71Volume",
  [NetworkMeasurementType.mass]: "FuelProrationEquationC71Mass",
  [NetworkMeasurementType.energy]: "FuelProrationEquationC71Energy",
} as const;
